import { SUPABASE_URL } from '../utils/supabase/url';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { toast } from 'sonner';
import { documentScopeAdapter } from '../features/documents/documentScope';
import { projectId as supabaseProjectId } from '../utils/supabase/info';
import { getAuthHeaders } from '../utils/supabase/client';
import { uploadWithProgress, batchPromises, buildProjectUploadRequest, STREAMING_UPLOAD_THRESHOLD_BYTES } from '../features/documents/documentManagementHelpers';
import type { DocumentControlMetadataInput } from '../document-control/metadata';
import type { ImportOutcome, TakeoffImportSource } from '../features/takeoff/importRunner';
import type { ImportPage } from '../features/takeoff/sheets/TakeoffUploader';
import type { PendingSitePhoto } from '../features/projects/site-photos/types';

export type UploadJobStatus = 'pending' | 'uploading' | 'success' | 'error';

/** Document, drawing import, Site Photo and download jobs all use the same
 *  transfer tray. Downloads reuse `status` ('pending' = preparing/queued,
 *  'uploading' = in flight), `stage` and an optional `percent`. */
export type UploadJobKind = 'file' | 'takeoff' | 'site-photo' | 'download';

export interface UploadJob {
  id: string;
  batchId: string;
  kind: UploadJobKind;
  fileName: string;
  fileSize: number;
  bytesSent: number;
  status: UploadJobStatus;
  error?: string;
  projectId: string;
  folderId: string;
  folderName: string;
  folderKey: string | null;
  /** Server response after success (used to surface the new file). */
  uploadedFile?: any;
  /** A failed document upload whose file is still held, so Retry can resend it. */
  retryable?: boolean;
  /** webkitRelativePath path-within-batch — for nested folder uploads. */
  relativePath?: string;
  // ── takeoff jobs only ──────────────────────────────────────────────────
  /** 0–100 across upload + sheet creation + carry-forward. Byte counts can't
   *  express the last two, which are the slow ones on a big drawing pack. */
  percent?: number;
  /** Short label for what the job is doing right now ("Uploading", "Adding
   *  drawings", "Checking revisions"). */
  stage?: string;
  /** Named on the row so a user three screens away knows WHICH project the
   *  drawings are landing in — the whole point of running this in the
   *  background is that they're somewhere else by now. */
  projectName?: string;
  /** How many sheets this source will add. */
  sheetCount?: number;
}

/** Percent complete for any job, whichever pipeline it came from. */
export const jobPercent = (job: UploadJob): number => {
  if (job.status === 'success') return 100;
  if (job.kind === 'site-photo') return job.status === 'error' ? 100 : 0;
  if (job.kind === 'download') return Math.min(100, Math.max(0, Math.round(job.percent ?? 0)));
  if (job.kind === 'takeoff') return Math.min(100, Math.max(0, Math.round(job.percent ?? 0)));
  if (job.fileSize <= 0) return 0;
  return Math.min(100, Math.round((job.bytesSent / job.fileSize) * 100));
};

interface FileUploadPayload {
  file: File;
  batchId: string;
  projectId: string;
  userId: string;
  /** The resolved destination, a new subfolder for files inside a dropped folder. */
  folderId: string;
  folderKey: string | null;
  documentControlMetadata?: DocumentControlMetadataInput;
}

interface EnqueueOptions {
  files: FileList | File[];
  projectId: string;
  userId: string;
  folderId: string;
  folderName: string;
  folderKey: string | null;
  /** Optional document-control metadata applied to every file in batch. */
  documentControlMetadata?: DocumentControlMetadataInput;
  /** Per-file metadata, keyed by the File object; wins over the batch-wide value. */
  documentControlMetadataByFile?: Map<File, DocumentControlMetadataInput>;
}

/** One source of a takeoff import batch: the file or Drive document, plus the
 *  pages of it the user confirmed in the review grid. */
export interface TakeoffImportItem {
  source: TakeoffImportSource;
  pages: ImportPage[];
}

interface TakeoffEnqueueOptions {
  projectId: string;
  /** Shown on the tray row. Falls back to "this project" when unknown. */
  projectName?: string;
  userId: string;
  items: TakeoffImportItem[];
}

interface SitePhotoEnqueueOptions {
  projectId: string;
  projectName?: string;
  photos: PendingSitePhoto[];
}

export interface DownloadPatch {
  stage?: string;
  /** 0-100 when the server reports it; leave undefined for an indeterminate row. */
  percent?: number;
  fileSize?: number;
  status?: UploadJobStatus;
}

interface UploadContextValue {
  jobs: UploadJob[];
  isExpanded: boolean;
  setExpanded: (next: boolean) => void;
  enqueueUploads: (opts: EnqueueOptions) => string;
  /** Queue a takeoff drawing import. Returns immediately — the work runs at
   *  the app root, so the caller (the uploader) can close and the user can go
   *  anywhere in the app while it finishes. */
  enqueueTakeoffImports: (opts: TakeoffEnqueueOptions) => string;
  enqueueSitePhotos: (opts: SitePhotoEnqueueOptions) => string;
  /** Add a download row to the tray. `onCancel` makes the row cancellable. */
  startDownload: (name: string, opts?: { stage?: string; onCancel?: () => void }) => string;
  updateDownload: (jobId: string, patch: DownloadPatch) => void;
  finishDownload: (jobId: string, result?: { error?: string; fileSize?: number }) => void;
  /** Runs the job's cancel handler and removes the row. */
  cancelDownload: (jobId: string) => void;
  /** Stops a document upload that has not finished and removes its row. */
  cancelUpload: (jobId: string) => void;
  retryJob: (jobId: string) => void;
  dismissCompleted: () => void;
  dismissAll: () => void;
}

const UploadContext = createContext<UploadContextValue>({
  jobs: [],
  isExpanded: true,
  setExpanded: () => {},
  enqueueUploads: () => '',
  enqueueTakeoffImports: () => '',
  enqueueSitePhotos: () => '',
  startDownload: () => '',
  updateDownload: () => {},
  finishDownload: () => {},
  cancelDownload: () => {},
  cancelUpload: () => {},
  retryJob: () => {},
  dismissCompleted: () => {},
  dismissAll: () => {},
});

export const useUploadContext = () => useContext(UploadContext);

const genId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export function UploadProvider({ children }: { children: ReactNode }) {
  const [jobs, setJobs] = useState<UploadJob[]>([]);
  const [isExpanded, setExpanded] = useState(true);

  // Live ref to jobs so the running uploader can read state without
  // re-subscribing to the closure every batch.
  const jobsRef = useRef<UploadJob[]>([]);
  useEffect(() => {
    jobsRef.current = jobs;
  }, [jobs]);

  // What each queued takeoff import needs to run — kept in a ref, not in job
  // state: it holds a real `File` and the confirmed page list, neither of which
  // any renderer needs, and keeping them out of state means a progress tick
  // doesn't push a large blob reference through every subscriber. Entries are
  // dropped as soon as their job can no longer be retried (see dropDismissed
  // and runTakeoffJob).
  const takeoffPayloadsRef = useRef<
    Map<string, { projectId: string; projectName?: string; userId: string; item: TakeoffImportItem }>
  >(new Map());
  const sitePhotoPayloadsRef = useRef<Map<string, { projectId: string; photo: PendingSitePhoto }>>(new Map());
  // A document upload keeps its File and resolved target until it succeeds or
  // its row is cleared, so a failure can be retried under the same command id
  // (the server's receipt makes the retry safe) and an upload can be stopped.
  const filePayloadsRef = useRef<Map<string, FileUploadPayload>>(new Map());
  const fileAbortRef = useRef<Map<string, AbortController>>(new Map());

  const updateJob = useCallback(
    (jobId: string, patch: Partial<UploadJob>) => {
      setJobs((prev) =>
        prev.map((j) => (j.id === jobId ? { ...j, ...patch } : j))
      );
    },
    []
  );

  // Clearing a row also drops whatever the job was holding on to (a takeoff
  // import keeps the picked File so it can be retried) — otherwise a morning
  // of drawing packs pins every one of them in memory until the tab is closed.
  const dropDismissed = useCallback((prev: UploadJob[]) => {
    const kept = prev.filter((j) => j.status === 'pending' || j.status === 'uploading');
    const keptIds = new Set(kept.map((j) => j.id));
    for (const job of prev) {
      if (!keptIds.has(job.id)) takeoffPayloadsRef.current.delete(job.id);
      if (!keptIds.has(job.id)) sitePhotoPayloadsRef.current.delete(job.id);
      if (!keptIds.has(job.id)) filePayloadsRef.current.delete(job.id);
    }
    return kept;
  }, []);

  const dismissCompleted = useCallback(() => {
    setJobs(dropDismissed);
  }, [dropDismissed]);

  const dismissAll = useCallback(() => {
    setJobs(dropDismissed);
  }, [dropDismissed]);

  /** Uploads one document job from its retained payload. Resolves to how it
   *  ended; a cancelled job has already left the tray. */
  const runFileJob = useCallback(async (jobId: string): Promise<'success' | 'error' | 'cancelled'> => {
    const payload = filePayloadsRef.current.get(jobId);
    if (!payload) return 'cancelled';
    const { file, projectId, userId, folderId: targetFolderId, folderKey, batchId } = payload;
    const controller = new AbortController();
    fileAbortRef.current.set(jobId, controller);
    updateJob(jobId, { status: 'uploading', bytesSent: 0, error: undefined, retryable: undefined });

    // Large files go up as a raw streamed body instead of multipart: the
    // server has to buffer a multipart body whole, and a file bigger than
    // the Edge Function worker's memory takes the worker — and every
    // request sharing it — down with it.
    const { url: uploadUrl, body: uploadBody } = buildProjectUploadRequest(
      documentScopeAdapter(projectId).url('upload'),
      file,
      {
        folderId: targetFolderId,
        userId,
        projectId,
        folderType: folderKey || 'unknown',
        documentControlMetadata: payload.documentControlMetadata,
        uploadCommandId: jobId,
      },
    );
    // Retry transient network-layer failures (flaky wifi, momentary
    // socket limits) with a short backoff instead of dropping the file.
    // Only network REJECTIONS are retried; an HTTP response (ok or not)
    // is returned to the handler below unchanged.
    const sendWithRetry = async () => {
      const MAX_ATTEMPTS = 4;
      for (let attempt = 1; ; attempt++) {
        try {
          return await uploadWithProgress(
            uploadUrl,
            uploadBody,
            await getAuthHeaders(),
            (bytesSent) => updateJob(jobId, { bytesSent }),
            // A streamed upload spends its whole life on the wire, so a
            // 300s cap aborts large models mid-flight on an ordinary
            // connection. Match the server's deadline rather than beat it
            // — the platform kills the request at ~400s anyway.
            file.size > STREAMING_UPLOAD_THRESHOLD_BYTES ? 390_000 : 300_000,
            controller.signal,
          );
        } catch (err) {
          if (controller.signal.aborted || attempt >= MAX_ATTEMPTS) throw err;
          // 250ms, 1s, 2.25s — give descriptors time to free up.
          await new Promise((r) => setTimeout(r, 250 * attempt * attempt));
        }
      }
    };

    try {
      const response = await sendWithRetry();
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        // GOOGLE_AUTH_REQUIRED: server returns 401 with a friendly
        // `message` field when the user's stored Drive token is
        // expired/revoked. We tag the thrown error so the catch
        // below can pop a "Reconnect" toast instead of dumping the
        // raw error code into the upload tray.
        const isAuthError =
          response.status === 401 && errBody?.error === 'GOOGLE_AUTH_REQUIRED';
        const err = new Error(
          errBody.message || errBody.error || `Upload failed for ${file.name}`,
        );
        if (isAuthError) (err as any).code = 'GOOGLE_AUTH_REQUIRED';
        throw err;
      }
      const data = await response.json();
      updateJob(jobId, {
        status: 'success',
        bytesSent: file.size,
        uploadedFile: data?.file,
      });
      // Nothing left to retry: let go of the File.
      filePayloadsRef.current.delete(jobId);
      if (data?.file) {
        window.dispatchEvent(
          new CustomEvent('quantis:document-uploaded', {
            detail: {
              projectId,
              folderId: targetFolderId,
              file: data.file,
              batchId,
            },
          })
        );
      }
      return 'success';
    } catch (err) {
      if (controller.signal.aborted) return 'cancelled';
      const isAuthError = (err as any)?.code === 'GOOGLE_AUTH_REQUIRED';
      const message = isAuthError
        ? 'Google Workspace connection expired — reconnect to upload.'
        : (err as any)?.name === 'TypeError' || ((err as any)?.message || '').includes('Failed to fetch')
          ? `Network error uploading ${file.name}`
          : err instanceof Error
            ? err.message
            : `Upload failed for ${file.name}`;
      updateJob(jobId, { status: 'error', error: message, retryable: true });
      // Pop a single actionable toast for auth errors. Sonner
      // dedupes by id so a 50-file upload that all fail with the
      // same auth error only shows ONE toast, not 50. Reconnect
      // uses the same OAuth + polling helper EmailView and
      // SystemSettings now share, so success closes the popup
      // and the user can retry the upload.
      if (isAuthError) {
        toast.error('Google Workspace connection expired', {
          id: 'upload-google-auth-required',
          description: 'Reconnect your Google account to keep uploading.',
          duration: 12_000,
          action: {
            label: 'Reconnect',
            onClick: async () => {
              try {
                const { GoogleCalendarService } = await import(
                  '../services/GoogleCalendarService'
                );
                const popup = await GoogleCalendarService.connect(userId);
                // Poll for the token landing in the DB so we can
                // close the popup and prompt the user to retry.
                const start = Date.now();
                const intervalMs = 1500;
                const maxMs = 120_000;
                const tick = async () => {
                  let ok = false;
                  try {
                    ok = await GoogleCalendarService.isConnected(userId);
                  } catch {
                    /* transient */
                  }
                  if (ok) {
                    try { popup?.close(); } catch { /* may already be gone */ }
                    toast.success('Reconnected — try the upload again.', {
                      id: 'upload-google-auth-required',
                    });
                    return;
                  }
                  if (Date.now() - start < maxMs) {
                    setTimeout(tick, intervalMs);
                  }
                };
                setTimeout(tick, intervalMs);
              } catch {
                toast.error('Couldn’t start Google sign-in.');
              }
            },
          },
        });
      }
      return 'error';
    } finally {
      if (fileAbortRef.current.get(jobId) === controller) fileAbortRef.current.delete(jobId);
    }
  }, [updateJob]);

  const runBatch = useCallback(
    async (
      batchId: string,
      jobIds: string[],
      opts: EnqueueOptions,
      filesByJob: Map<string, File>
    ) => {
      const { projectId, userId, folderId, folderKey, documentControlMetadata: batchMetadata, documentControlMetadataByFile } = opts;

      // ── 1. Group files by their relative folder path so we can build the
      // tree of subfolders before uploading. (Folder picks via
      // <input webkitdirectory> and folder drops expanded by
      // collectFilesFromDataTransfer both set webkitRelativePath; flat
      // file picks have it empty.)
      const filesByPath = new Map<string, Array<{ jobId: string; file: File }>>();
      const folderPaths = new Set<string>();

      jobIds.forEach((jobId) => {
        const file = filesByJob.get(jobId);
        if (!file) return;
        const relativePath = (file as any).webkitRelativePath || '';
        if (relativePath) {
          const parts = relativePath.split('/');
          parts.pop();
          const folderPath = parts.join('/');
          let cursor = '';
          parts.forEach((part: string) => {
            cursor = cursor ? `${cursor}/${part}` : part;
            folderPaths.add(cursor);
          });
          if (!filesByPath.has(folderPath)) filesByPath.set(folderPath, []);
          filesByPath.get(folderPath)!.push({ jobId, file });
        } else {
          if (!filesByPath.has('')) filesByPath.set('', []);
          filesByPath.get('')!.push({ jobId, file });
        }
      });

      // ── 2. Create subfolders breadth-first.
      const folderIdMap = new Map<string, string>();
      folderIdMap.set('', folderId);
      const sortedFolderPaths = Array.from(folderPaths).sort(
        (a, b) => a.split('/').length - b.split('/').length
      );

      try {
        for (const path of sortedFolderPaths) {
          const parts = path.split('/');
          const folderName = parts[parts.length - 1];
          const parentPath = parts.slice(0, -1).join('/');
          const parentFolderId = folderIdMap.get(parentPath) || folderId;

          const response = await fetch(
            documentScopeAdapter(projectId).url('create-subfolder'),
            {
              method: 'POST',
              headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
              body: JSON.stringify({
                userId,
                folderName,
                parentFolderId,
              }),
            }
          );
          if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || `Failed to create folder: ${folderName}`);
          }
          const data = await response.json();
          folderIdMap.set(path, data.folderId);
        }
      } catch (folderErr) {
        // Mark every job in this batch as errored — we never reached upload.
        const message = folderErr instanceof Error ? folderErr.message : 'Failed to prepare folders';
        jobIds.forEach((jobId) =>
          updateJob(jobId, { status: 'error', error: message })
        );
        toast.error(message);
        return;
      }

      // ── 3. Run uploads in parallel. Per-file progress is mirrored into
      // the job state, and each success announces the new file so a mounted
      // document view can append it without a reload.
      const folderIdsForInvalidation = new Set<string>();
      let successCount = 0;
      let errorCount = 0;

      // Cap concurrent uploads so dropping a 30-file folder doesn't fire 30
      // parallel XHRs into Supabase. (Historical note: the persistent
      // net::ERR_ACCESS_DENIED at xhr.send() blamed on descriptor exhaustion
      // was actually unreadable directory Files from dataTransfer.files —
      // fixed at the drop zone via collectFilesFromDataTransfer. The cap and
      // retry stay for genuinely transient network failures.)
      const PARALLEL_UPLOADS = 3;
      // Payloads are held up front, so Stop on a row still waiting its turn
      // removes its payload and the worker below skips it.
      for (const jobId of jobIds) {
        const file = filesByJob.get(jobId);
        if (!file) continue;
        const relativePath = (file as any).webkitRelativePath || '';
        const folderPath = relativePath
          ? relativePath.split('/').slice(0, -1).join('/')
          : '';
        const targetFolderId = folderIdMap.get(folderPath) || folderId;
        folderIdsForInvalidation.add(targetFolderId);
        filePayloadsRef.current.set(jobId, {
          file, batchId, projectId, userId, folderId: targetFolderId, folderKey,
          documentControlMetadata: documentControlMetadataByFile?.get(file) ?? batchMetadata,
        });
      }
      await batchPromises(jobIds, PARALLEL_UPLOADS, async (jobId) => {
        const outcome = await runFileJob(jobId);
        if (outcome === 'success') successCount++;
        else if (outcome === 'error') errorCount++;
      });

      // ── 4. Batch finished. Invalidate caches in any mounted view.
      window.dispatchEvent(
        new CustomEvent('quantis:upload-batch-complete', {
          detail: {
            projectId,
            folderIds: Array.from(folderIdsForInvalidation),
            batchId,
            successCount,
            errorCount,
          },
        })
      );

      // No batch summary record: the server writes a "Document Uploaded"
      // activity per file, and a second row carrying a JSON count of the
      // same files only repeated them on the timeline.

      // Toast summary — keeps the user informed even if the panel is closed.
      if (errorCount === 0) {
        toast.success(
          successCount === 1
            ? `Uploaded ${successCount} file`
            : `Uploaded ${successCount} files`
        );
      } else if (successCount === 0) {
        toast.error(`Upload failed for ${errorCount} file${errorCount === 1 ? '' : 's'}`);
      } else {
        toast.warning(
          `Uploaded ${successCount} file${successCount === 1 ? '' : 's'}, ${errorCount} failed`
        );
      }
    },
    [updateJob, runFileJob]
  );

  // ── Takeoff drawing imports ───────────────────────────────────────────────
  /** Run one queued takeoff source. Resolves to its outcome, or null if it
   *  failed (the job row carries the error either way). */
  const runTakeoffJob = useCallback(async (jobId: string): Promise<{ landed: number; outcome: ImportOutcome } | null> => {
    const payload = takeoffPayloadsRef.current.get(jobId);
    if (!payload) return null;
    // Lazy so the takeoff import path — and the services it drags in — stays
    // out of the eager app-shell bundle that AppProviders loads on every page.
    const { runTakeoffImport } = await import('../features/takeoff/importRunner');
    updateJob(jobId, { status: 'uploading', percent: 0, stage: 'Starting', error: undefined });
    try {
      const result = await runTakeoffImport({
        projectId: payload.projectId,
        userId: payload.userId,
        source: payload.item.source,
        pages: payload.item.pages,
        onProgress: (pct) => updateJob(jobId, { percent: pct }),
        onStage: (stage) => updateJob(jobId, { stage }),
      });
      updateJob(jobId, { status: 'success', percent: 100, stage: undefined });
      // Nothing left to retry — let go of the File rather than holding it
      // until the user gets round to dismissing the tray.
      takeoffPayloadsRef.current.delete(jobId);
      return { landed: result.landed.length, outcome: result.outcome };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Import failed';
      updateJob(jobId, { status: 'error', stage: undefined, error: message });
      return null;
    }
  }, [updateJob]);

  const runTakeoffBatch = useCallback(async (jobIds: string[]) => {
    const { summariseImport, emptyImportOutcome } = await import('../features/takeoff/importRunner');
    const totals = emptyImportOutcome();
    let landedTotal = 0;
    const failed: string[] = [];

    // Sequential, not parallel: each source supersedes against the project's
    // live sheets, so two running at once could both retire the same earlier
    // revision (or miss each other's) — and Drive would rate-limit the uploads
    // anyway. Same order the uploader queued them in.
    for (const jobId of jobIds) {
      const result = await runTakeoffJob(jobId);
      if (!result) {
        failed.push(takeoffPayloadsRef.current.get(jobId)?.item.source.name ?? 'a drawing');
        continue;
      }
      landedTotal += result.landed;
      totals.added += result.outcome.added;
      totals.superseded += result.outcome.superseded;
      totals.carried += result.outcome.carried;
      totals.sizeChanged += result.outcome.sizeChanged;
      totals.unmatched += result.outcome.unmatched;
      totals.failed.push(...result.outcome.failed);
      totals.supersedeFailed.push(...result.outcome.supersedeFailed);
    }

    // ONE toast for the batch — the user is elsewhere in the app by now, and
    // six separate toasts for a six-file pack would bury whatever they moved
    // on to. The tray rows carry the per-source detail.
    if (landedTotal > 0) {
      const summary = summariseImport(landedTotal, totals);
      const description = failed.length > 0
        ? [summary.description, `Could not import: ${failed.join(', ')}`].filter(Boolean).join('. ')
        : summary.description;
      if (summary.level === 'warning' || failed.length > 0) {
        toast.warning(summary.title, { description, duration: 8000 });
      } else {
        toast.success(summary.title, { description });
      }
    } else if (failed.length > 0) {
      toast.error(
        `Couldn't import ${failed.length} drawing file${failed.length === 1 ? '' : 's'}`,
        { description: failed.join(', '), duration: 8000 },
      );
    }
  }, [runTakeoffJob]);

  const enqueueTakeoffImports = useCallback((opts: TakeoffEnqueueOptions) => {
    if (opts.items.length === 0) return '';
    const batchId = genId();
    const newJobs: UploadJob[] = opts.items.map((item) => {
      const id = genId();
      takeoffPayloadsRef.current.set(id, {
        projectId: opts.projectId,
        projectName: opts.projectName,
        userId: opts.userId,
        item,
      });
      return {
        id,
        batchId,
        kind: 'takeoff' as const,
        fileName: item.source.name,
        fileSize: item.source.size ?? 0,
        bytesSent: 0,
        status: 'pending' as const,
        projectId: opts.projectId,
        projectName: opts.projectName,
        // The takeoff pipeline picks its own destination folder (the project's
        // "01 - Take-Offs & Measures"); these exist only to satisfy the shared
        // job shape and are never shown on a takeoff row.
        folderId: '',
        folderName: '',
        folderKey: null,
        percent: 0,
        stage: 'Queued',
        sheetCount: item.pages.length,
      };
    });

    setJobs((prev) => [...prev, ...newJobs]);
    setExpanded(true);
    void runTakeoffBatch(newJobs.map((j) => j.id));
    return batchId;
  }, [runTakeoffBatch]);

  const runSitePhotoJob = useCallback(async (jobId: string) => {
    const payload = sitePhotoPayloadsRef.current.get(jobId);
    if (!payload) return false;
    updateJob(jobId, { status: 'uploading', stage: 'Uploading', error: undefined });
    try {
      const { SitePhotoService } = await import('../services/SitePhotoService');
      const item = await SitePhotoService.upload({
        projectId: payload.projectId,
        jpeg: payload.photo.jpeg,
        capturedAt: payload.photo.capturedAt,
        location: payload.photo.location,
      });
      updateJob(jobId, { status: 'success', bytesSent: payload.photo.jpeg.size, stage: undefined });
      sitePhotoPayloadsRef.current.delete(jobId);
      window.dispatchEvent(new CustomEvent('quantis:site-photo-uploaded', {
        detail: { projectId: payload.projectId, item },
      }));
      return true;
    } catch (error) {
      updateJob(jobId, {
        status: 'error',
        stage: undefined,
        error: error instanceof Error ? error.message : 'Photo upload failed',
      });
      return false;
    }
  }, [updateJob]);

  const runSitePhotoBatch = useCallback(async (jobIds: string[]) => {
    let next = 0;
    let uploaded = 0;
    const worker = async () => {
      while (next < jobIds.length) {
        const jobId = jobIds[next++];
        if (await runSitePhotoJob(jobId)) uploaded++;
      }
    };
    await Promise.all(Array.from({ length: Math.min(3, jobIds.length) }, worker));
    const failed = jobIds.length - uploaded;
    if (failed === 0) toast.success(`Uploaded ${uploaded} site photo${uploaded === 1 ? '' : 's'}`);
    else if (uploaded === 0) toast.error(`${failed} site photo upload${failed === 1 ? '' : 's'} failed`);
    else toast.warning(`Uploaded ${uploaded} site photo${uploaded === 1 ? '' : 's'}, ${failed} failed`);
  }, [runSitePhotoJob]);

  const enqueueSitePhotos = useCallback((opts: SitePhotoEnqueueOptions) => {
    if (opts.photos.length === 0) return '';
    const batchId = genId();
    const newJobs: UploadJob[] = opts.photos.map((photo, index) => {
      const id = genId();
      sitePhotoPayloadsRef.current.set(id, { projectId: opts.projectId, photo });
      return {
        id, batchId, kind: 'site-photo',
        fileName: photo.fileName || `Site photo ${index + 1}.jpg`,
        fileSize: photo.jpeg.size,
        bytesSent: 0,
        status: 'pending',
        projectId: opts.projectId,
        projectName: opts.projectName,
        folderId: '', folderName: '', folderKey: null,
        stage: 'Queued',
      };
    });
    setJobs((prev) => [...prev, ...newJobs]);
    setExpanded(true);
    void runSitePhotoBatch(newJobs.map((job) => job.id));
    return batchId;
  }, [runSitePhotoBatch]);

  const downloadCancelRef = useRef<Map<string, () => void>>(new Map());

  const startDownload = useCallback((name: string, opts?: { stage?: string; onCancel?: () => void }) => {
    const id = genId();
    if (opts?.onCancel) downloadCancelRef.current.set(id, opts.onCancel);
    setJobs((prev) => [
      ...prev,
      {
        id, batchId: id, kind: 'download', fileName: name, fileSize: 0, bytesSent: 0,
        status: 'pending', stage: opts?.stage ?? 'Preparing',
        projectId: '', folderId: '', folderName: '', folderKey: null,
      },
    ]);
    setExpanded(true);
    return id;
  }, []);

  const updateDownload = useCallback((jobId: string, patch: DownloadPatch) => {
    updateJob(jobId, patch);
  }, [updateJob]);

  const finishDownload = useCallback((jobId: string, result?: { error?: string; fileSize?: number }) => {
    downloadCancelRef.current.delete(jobId);
    if (result?.error) updateJob(jobId, { status: 'error', error: result.error, stage: undefined });
    else updateJob(jobId, { status: 'success', percent: 100, stage: undefined, ...(result?.fileSize ? { fileSize: result.fileSize } : {}) });
  }, [updateJob]);

  const cancelDownload = useCallback((jobId: string) => {
    downloadCancelRef.current.get(jobId)?.();
    downloadCancelRef.current.delete(jobId);
    setJobs((prev) => prev.filter((j) => j.id !== jobId));
  }, []);

  const cancelUpload = useCallback((jobId: string) => {
    filePayloadsRef.current.delete(jobId);
    fileAbortRef.current.get(jobId)?.abort();
    fileAbortRef.current.delete(jobId);
    setJobs((prev) => prev.filter((j) => j.id !== jobId));
  }, []);

  /** Re-run a failed document upload, drawing import or Site Photo job. */
  const retryJob = useCallback((jobId: string) => {
    const filePayload = filePayloadsRef.current.get(jobId);
    if (filePayload) {
      void runFileJob(jobId).then((outcome) => {
        if (outcome === 'cancelled') return;
        window.dispatchEvent(new CustomEvent('quantis:upload-batch-complete', {
          detail: {
            projectId: filePayload.projectId,
            folderIds: [filePayload.folderId],
            batchId: filePayload.batchId,
            successCount: outcome === 'success' ? 1 : 0,
            errorCount: outcome === 'error' ? 1 : 0,
          },
        }));
        if (outcome === 'success') toast.success(`Uploaded ${filePayload.file.name}`);
      });
      return;
    }
    if (sitePhotoPayloadsRef.current.has(jobId)) {
      void runSitePhotoJob(jobId).then((ok) => {
        if (ok) toast.success('Site photo uploaded');
      });
      return;
    }
    if (!takeoffPayloadsRef.current.has(jobId)) return;
    void (async () => {
      const { summariseImport } = await import('../features/takeoff/importRunner');
      const result = await runTakeoffJob(jobId);
      if (!result) return;
      const summary = summariseImport(result.landed, result.outcome);
      if (summary.level === 'warning') toast.warning(summary.title, { description: summary.description, duration: 8000 });
      else toast.success(summary.title, { description: summary.description });
    })();
  }, [runTakeoffJob, runSitePhotoJob, runFileJob]);

  // Warn before a reload or close drops any work still in flight.
  useEffect(() => {
    const inflight = jobs.some((j) => j.status === 'pending' || j.status === 'uploading');
    if (!inflight) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [jobs]);

  const enqueueUploads = useCallback(
    (opts: EnqueueOptions) => {
      const fileArray = Array.from(opts.files);
      if (fileArray.length === 0) return '';

      const batchId = genId();
      const newJobs: UploadJob[] = fileArray.map((file) => {
        const id = genId();
        return {
          id,
          batchId,
          kind: 'file' as const,
          fileName: file.name,
          fileSize: file.size,
          bytesSent: 0,
          status: 'pending',
          projectId: opts.projectId,
          folderId: opts.folderId,
          folderName: opts.folderName,
          folderKey: opts.folderKey,
          relativePath: (file as any).webkitRelativePath || undefined,
        };
      });

      const filesByJob = new Map<string, File>();
      newJobs.forEach((job, idx) => filesByJob.set(job.id, fileArray[idx]));

      setJobs((prev) => [...prev, ...newJobs]);
      setExpanded(true);

      // Kick off the batch — fire and forget; jobs update via setJobs.
      void runBatch(
        batchId,
        newJobs.map((j) => j.id),
        opts,
        filesByJob
      );

      return batchId;
    },
    [runBatch]
  );

  const value = useMemo<UploadContextValue>(
    () => ({
      jobs,
      isExpanded,
      setExpanded,
      enqueueUploads,
      enqueueTakeoffImports,
      enqueueSitePhotos,
      startDownload,
      updateDownload,
      finishDownload,
      cancelDownload,
      cancelUpload,
      retryJob,
      dismissCompleted,
      dismissAll,
    }),
    [jobs, isExpanded, enqueueUploads, enqueueTakeoffImports, enqueueSitePhotos, startDownload, updateDownload, finishDownload, cancelDownload, cancelUpload, retryJob, dismissCompleted, dismissAll]
  );

  return <UploadContext.Provider value={value}>{children}</UploadContext.Provider>;
}
